import { addMinutes } from 'date-fns';

import { formatTime } from '@sophocles/utils/formatUtils';

export function scheduleStart(isAlwaysOn, onTime, onType, onOffset, nextSunrise, nextSunset, user, t) {
  if (isAlwaysOn) return t('sites.alwaysOn');

  const baseSunTime = onType === 'sunset' ? nextSunset : onType == 'sunrise' ? nextSunrise : null;
  const adjustedSunTime = baseSunTime != null ? addMinutes(baseSunTime, onOffset ?? 0) : null;

  if (onType === 'sunset') {
    if (!onOffset || onOffset === 0) {
      if (adjustedSunTime) {
        return `${t('sites.sunset')} (${formatTime(adjustedSunTime, user)})`;
      }
      return t('sites.sunset');
    }
    const timeString = `${t('sites.sunset')} ` + (onOffset < 0 ? `-${Math.abs(onOffset)}` : `+${Math.abs(onOffset)}`);
    if (adjustedSunTime) {
      return `${timeString} (${formatTime(adjustedSunTime, user)})`;
    }
    return timeString;
  }

  if (onType === 'sunrise') {
    if (!onOffset || onOffset === 0) {
      if (adjustedSunTime) {
        return `${t('sites.sunrise')} (${formatTime(adjustedSunTime, user)})`;
      }
      return t('sites.sunrise');
    }
    const timeString = `${t('sites.sunrise')} ` + (onOffset < 0 ? `-${Math.abs(onOffset)}` : `+${Math.abs(onOffset)}`);
    if (adjustedSunTime) {
      return `${timeString} (${formatTime(adjustedSunTime, user)})`;
    }
    return timeString;
  }

  return formatTime(onTime, user);
}

export function scheduleEnd(isAlwaysOn, offTime, offType, offOffset, nextSunrise, nextSunset, user, t) {
  if (isAlwaysOn) return t('sites.alwaysOn');

  const baseSunTime = offType === 'sunset' ? nextSunset : offType == 'sunrise' ? nextSunrise : null;
  const adjustedSunTime = baseSunTime != null ? addMinutes(baseSunTime, offOffset ?? 0) : null;

  if (offType === 'sunset') {
    if (!offOffset || offOffset === 0) {
      if (adjustedSunTime) {
        return `${t('sites.sunset')} (${formatTime(adjustedSunTime, user)})`;
      }
      return t('sites.sunset');
    }
    const timeString =
      `${t('sites.sunset')} ` + (offOffset < 0 ? `-${Math.abs(offOffset)}` : `+${Math.abs(offOffset)}`);
    if (adjustedSunTime) {
      return `${timeString} (${formatTime(adjustedSunTime, user)})`;
    }
    return timeString;
  }

  if (offType === 'sunrise') {
    if (!offOffset || offOffset === 0) {
      if (adjustedSunTime) {
        return `${t('sites.sunrise')} (${formatTime(adjustedSunTime, user)})`;
      }
      return t('sites.sunrise');
    }
    const timeString =
      `${t('sites.sunrise')} ` + (offOffset < 0 ? `-${Math.abs(offOffset)}` : `+${Math.abs(offOffset)}`);
    if (adjustedSunTime) {
      return `${timeString} (${formatTime(adjustedSunTime, user)})`;
    }
    return timeString;
  }

  return formatTime(offTime, user);
}

// days_of_week bitmask: bits 1–7 hold Mon–Sun (bit 0 unused). 0xFE (254) =
// every day. The canonical "all days" sentinel for relay schedules.
export const ALL_DAYS_MASK = 0xfe;

/**
 * Decode a days_of_week bitmask into a 7-element boolean array in Mon→Sun
 * order ([Mon, Tue, Wed, Thu, Fri, Sat, Sun]). The single source of truth for
 * the bit layout (bit 1<<(i+1) = day i) and the all-days sentinel.
 *
 * @param {number|null} daysOfWeek
 * @returns {boolean[]} 7 booleans, Mon→Sun
 */
export function daysOfWeekToBooleans(daysOfWeek) {
  if (typeof daysOfWeek !== 'number' || daysOfWeek === ALL_DAYS_MASK) {
    return [true, true, true, true, true, true, true];
  }
  return Array.from({ length: 7 }, (_, i) => !!(daysOfWeek & (1 << (i + 1))));
}

export function scheduleDays(daysOfWeek, t) {
  const realDays = [
    t('general.monday'),
    t('general.tuesday'),
    t('general.wednesday'),
    t('general.thursday'),
    t('general.friday'),
    t('general.saturday'),
    t('general.sunday'),
  ];

  // If daysOfWeek is an integer, then it is a bitmask for days of week.
  if (typeof daysOfWeek === 'number') {
    daysOfWeek = daysOfWeekToBooleans(daysOfWeek);
  }

  // If daysOfWeek is an array of booleans, convert to indices
  if (Array.isArray(daysOfWeek) && daysOfWeek.length && typeof daysOfWeek[0] === 'boolean') {
    daysOfWeek = daysOfWeek.map((val, idx) => (val ? idx : null)).filter(idx => idx !== null);
  }

  if (!Array.isArray(daysOfWeek) || daysOfWeek.length === 0) {
    return realDays.map(d => d.substring(0, 3)); // Return all translated days if input is null, undefined, or empty
  }

  return daysOfWeek
    .filter(dayIndex => dayIndex >= 0 && dayIndex < realDays.length) // Prevent out-of-bounds errors
    .map(dayIndex => realDays[dayIndex].substring(0, 3)); // Get only the first 3 letters
}

export function dimmingData(schedule) {
  const value = schedule.get('duty_cycle', 100);
  const displayValue = value;

  return 'Brightness (%): ' + displayValue;
}
